"use client"

import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
} from "@mui/material"
import type React from "react"
import { useState } from "react"
import { MenuIcon, UserIcon, LogOutIcon, DoorClosedIcon as CloseIcon } from "lucide-react"
import { useNavigate } from "react-router"
import { useUser } from "@/contexts/UserContext"
import logo from "../assets/logo.png" // Import the logo

const HeaderBtn = ({ children, href, onClick }: { children: React.ReactNode; href: string; onClick?: () => void }) => {
  const navigate = useNavigate()

  const handleClick = () => {
    if (href.startsWith("http")) {
      window.location.href = href
    } else if (href === "/") {
      window.location.href = "/"
    } else {
      navigate(href)
    }
    if (onClick) onClick()
  }

  return (
    <Button
      color="primary"
      sx={{
        fontSize: "20px",
        textTransform: "none",
        fontFamily: "Roboto",
        fontStyle: "normal",
        lineHeight: "20px",
        color: "#0070C0",
        fontWeight: 500,
        padding: "8px 15px",
      }}
      onClick={handleClick}
    >
      {children}
    </Button>
  )
}

export default function DttHeader() {
  const { user } = useUser()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    document.body.classList.remove("ro-popover-open")
    setAnchorEl(event.currentTarget)
  }

  const handleUserMenuClose = () => {
    setAnchorEl(null)
  }

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  const menuItems = [
    { label: "Home", href: "/" },
    { label: "Support", href: process.env.URL_SUPPORT! },
    { label: "About Us", href: process.env.URL_ABOUTUS! },
    { label: "Community", href: `https://${process.env.API_HOST}/com` },
  ]

  return (
    <AppBar
      position="static"
      color="transparent"
      sx={(theme) => ({
        zIndex: theme.zIndex.drawer + 1,
        boxShadow: "unset",
        padding: "10px",
        backgroundColor: "#fff",
      })}
    >
      <Toolbar
        sx={{
          "& .MuiButtonBase-root": {
            fontFamily: "sans-serif",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "20px",
            lineHeight: "22px",
            color: "#0070C0",
          },
        }}
      >
        <img
          src={logo || "/placeholder.svg"}
          alt="logo"
          style={{
            width: isMobile ? "140px" : "180px",
            marginRight: "10px",
            cursor: 'pointer'
          }}
          onClick={() => window.location.href = "/"}
        />

        <Box sx={{ flexGrow: 1 }} />

        {isMobile ? (
          <>
            <IconButton color="primary" onClick={handleMobileMenuToggle} sx={{ ml: 2 }}>
              {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>

            <Drawer
              anchor="right"
              open={mobileMenuOpen}
              onClose={handleMobileMenuToggle}
              sx={{
                "& .MuiDrawer-paper": {
                  width: "80%",
                  maxWidth: "300px",
                  pt: 2,
                  px: 2,
                },
              }}
            >
              <List style={{ marginTop: isMobile ? "30%" : "0" }}>
                {menuItems.map((item) => (
                  <ListItem key={item.label}>
                    <HeaderBtn href={item.href} onClick={() => setMobileMenuOpen(false)}>
                      {item.label}
                    </HeaderBtn>
                  </ListItem>
                ))}
                <Divider sx={{ my: 2 }} />
                <ListItem>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleUserMenuClick}
                    color="primary"
                    title={user?.display_name}
                    sx={{
                      textTransform: "none",
                      borderRadius: "30px",
                      "& .MuiTypography-root": {
                        color: "white",
                      },
                      fontFamily: "Roboto",
                      fontStyle: "normal",
                      fontWeight: 700,
                      fontSize: "16px",
                      lineHeight: "19px",
                    }}
                  >
                    <Typography variant="h6" noWrap component="div" ml={1}>
                      {user?.display_name}
                    </Typography>
                  </Button>
                </ListItem>
              </List>
            </Drawer>
          </>
        ) : (
          <>
            {menuItems.map((item) => (
              <HeaderBtn key={item.label} href={item.href}>
                {item.label}
              </HeaderBtn>
            ))}
            <Button
              size="small"
              variant="contained"
              onClick={handleUserMenuClick}
              color="primary"
              title={user?.display_name}
              sx={{
                textTransform: "none",
                borderRadius: "30px",
                "& .MuiTypography-root": {
                  color: "white",
                },
                fontFamily: "Roboto",
                fontStyle: "normal",
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "19px",
                textDecoration: "none",
                color: "#F5F5F5",
                marginLeft: "50px",
              }}
            >
              <Typography variant="h6" noWrap component="div" ml={1}>
                {user?.display_name}
              </Typography>
            </Button>
          </>
        )}

        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          open={Boolean(anchorEl)}
          onClose={handleUserMenuClose}
        >
          <MenuItem onClick={() => window.location.reload()}>
            <UserIcon size={20} />
            <Typography variant="inherit" noWrap component="span" ml={1}>
              Account
            </Typography>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => (window.location.href = user?.logout_url || "/")}>
            <LogOutIcon size={20} />
            <Typography variant="inherit" noWrap component="span" ml={1}>
              Logout
            </Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  )
}

